import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { getAnnouncements, createAnnouncement, generateNotice } from '@/services/api';
import { Plus, Bell, AlertCircle, Sparkles, Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal'
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const data = await getAnnouncements();
      setAnnouncements(data);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createAnnouncement(formData);
      toast.success('Announcement posted successfully');
      setDialogOpen(false);
      setFormData({ title: '', content: '', priority: 'normal' });
      setAiInstruction('');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to post announcement');
    }
  };

  const handleAIGenerate = async () => {
    if (!aiInstruction.trim()) {
      toast.error('Please enter a brief instruction first');
      return;
    }
    
    setIsGenerating(true);
    try {
      const result = await generateNotice(aiInstruction, formData.priority);
      setFormData({
        ...formData,
        title: result.suggested_title,
        content: result.generated_content
      });
      toast.success('Notice generated by AI!');
    } catch (error) {
      toast.error('Failed to generate notice');
    } finally {
      setIsGenerating(false);
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      normal: { variant: 'secondary', label: 'Normal' },
      important: { variant: 'default', label: 'Important' },
      urgent: { variant: 'destructive', label: 'Urgent' }
    };
    const item = config[priority] || config.normal;
    return <Badge variant={item.variant}>{item.label}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-announcements">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Announcements</h1>
          <p className="text-slate-600">Communicate with residents</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-secondary hover:bg-secondary/90" data-testid="new-announcement-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Post New Announcement</DialogTitle>
              <DialogDescription>Share important updates with all residents</DialogDescription>
            </DialogHeader>
            
            {/* AI Notice Generator Section */}
            <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-4 rounded-lg border border-secondary/20 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 className="h-5 w-5 text-secondary" />
                <span className="font-semibold text-slate-900">AI Smart Notice Generator</span>
              </div>
              <div className="space-y-3">
                <div>
                  <Input
                    placeholder="Enter a brief instruction (e.g., 'Water tank cleaning tomorrow 9am to 2pm')"
                    value={aiInstruction}
                    onChange={(e) => setAiInstruction(e.target.value)}
                    data-testid="ai-instruction-input"
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleAIGenerate}
                  disabled={isGenerating || !aiInstruction.trim()}
                  className="w-full bg-secondary hover:bg-secondary/90"
                  data-testid="ai-generate-btn"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating Notice...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Generate Professional Notice
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">Or write manually</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  placeholder="Announcement title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="announcement-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  placeholder="Announcement details"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  data-testid="announcement-content-input"
                  rows={5}
                />
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                  <SelectTrigger data-testid="announcement-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="important">Important</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-secondary hover:bg-secondary/90" data-testid="announcement-submit-btn">Post Announcement</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {announcements.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No announcements yet</p>
            </CardContent>
          </Card>
        ) : (
          announcements.map((announcement) => (
            <Card key={announcement.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-testid="announcement-item">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Bell className="h-5 w-5 text-secondary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900">{announcement.title}</h3>
                      <p className="text-sm text-slate-500">Posted by {announcement.posted_by_name}</p>
                    </div>
                  </div>
                  {getPriorityBadge(announcement.priority)}
                </div>
                <p className="text-slate-600 mb-4 leading-relaxed">{announcement.content}</p>
                <div className="text-xs text-slate-500">
                  {new Date(announcement.created_at).toLocaleString()}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminAnnouncements;